/**
 * One project, end to end.
 *
 * The company-wide tabs answer "how is the business doing"; this answers "what
 * is happening on this job", which is a different question asked by a different
 * person. Everything here is the same data those tabs show, filtered to one
 * project — the API already takes `projectId` on every list — and every form is
 * the same component, with the project fixed instead of picked. Nothing here
 * can record a cost against the wrong job, because there is nothing to get
 * wrong.
 *
 * Each section is its own page under /c/:slug/projects/:id/:section rather than
 * one long scroll: these are separate jobs of work, and a draw schedule and a
 * commission list have nothing to say to each other. Only the section on screen
 * fetches, so opening a project costs one round of queries rather than ten.
 */
import { ZERO, add, sub } from "@dantria/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import {
  ApiError,
  api,
  fmtAmount as fmt,
  formatPercent,
  rateFromPercent,
  rateToPercent,
  unwrap,
} from "../api";
import {
  Button,
  ChevronButton,
  DeleteButton,
  Empty,
  ErrorNote,
  Field,
  HoverCard,
  Loading,
  Modal,
  Panel,
  PercentInput,
  SortTh,
  Stat,
  Table,
  TableSearch,
  Td,
  Th,
  input,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { useResource } from "../useResource";
import {
  ChangeOrderAction,
  ChangeOrderStatus,
  ChangeOrders,
  CommissionForm,
  type CommissionFormInitial,
  CommitmentEarnings,
  CommitmentForm,
  ExpenseForm,
  type ExpenseFormInitial,
  PaymentsList,
  RecordPaymentForm,
  ScheduleRowsForm,
  type Settlement,
  type SettlementPayment,
} from "./shared";

async function listProjectExpenses(slug: string, projectId: string) {
  return unwrap(await api.api.c[":slug"].expenses.$get({ param: { slug }, query: { projectId } }));
}

type ProjectExpenseRow = Awaited<ReturnType<typeof listProjectExpenses>>["expenses"][number];

async function fetchProjectStatusEvents(slug: string, projectId: string) {
  return unwrap(
    await api.api.c[":slug"].projects[":id"]["status-events"].$get({
      param: { slug, id: projectId },
    }),
  );
}

type ProjectStatusEvent = Awaited<ReturnType<typeof fetchProjectStatusEvents>>["events"][number];

function expenseToInitial(e: ProjectExpenseRow): ExpenseFormInitial {
  return {
    scope: e.scope,
    projectId: e.projectId,
    costCodeId: e.costCodeId,
    text: e.description,
    amount: e.amount,
    date: e.incurredOn,
  };
}

async function listProjectCommissions(slug: string, projectId: string) {
  return unwrap(
    await api.api.c[":slug"].commissions.$get({ param: { slug }, query: { projectId } }),
  );
}

type ProjectCommissionRow = Awaited<
  ReturnType<typeof listProjectCommissions>
>["commissions"][number];

function commissionToInitial(cm: ProjectCommissionRow): CommissionFormInitial {
  return {
    scope: cm.scope,
    projectId: cm.projectId,
    clientId: cm.clientId,
    contractorId: cm.contractorId,
    description: cm.description,
    reference: cm.reference,
    amount: cm.amount,
    earnedOn: cm.earnedOn,
  };
}

/**
 * Correct what a contractor is paid. Lives in a dialog, so the fields open
 * over the row they belong to rather than pushing the table around.
 *
 * Only the number that defines the deal is offered — the sum for a fixed
 * commitment, the percentage for a share — because the basis decides which
 * of the two means anything and the API refuses the other. Changing the basis
 * itself would re-price every draw already billed against it, so that stays a
 * delete-and-recreate. The fixed field starts from `percentBase`, which is the
 * number that was typed; `contractValue` is net of charge-backs, and saving it
 * back would quietly bake those in twice.
 */
function CommitmentEditForm({
  commitment,
  currency,
  busy,
  onCancel,
  onSubmit,
}: {
  commitment: {
    contractorName: string;
    scope: string | null;
    basis: string;
    percent: string | null;
    percentBase: string;
  };
  currency: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: { scope: string; contractValue?: string; percent?: string }) => void;
}) {
  const share = commitment.basis === "percent_of_contract";
  const [scope, setScope] = useState(commitment.scope ?? "");
  const [value, setValue] = useState(commitment.percentBase);
  const [percent, setPercent] = useState(rateToPercent(commitment.percent ?? "0"));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          scope,
          ...(share ? { percent: rateFromPercent(percent) } : { contractValue: value }),
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Scope">
          <input className={input} value={scope} onChange={(e) => setScope(e.target.value)} />
        </Field>
        {share ? (
          <Field label="Share of the contract" hint="The base comes from the contract">
            <PercentInput value={percent} onChange={setPercent} required />
          </Field>
        ) : (
          <Field label={`Value (${currency})`}>
            <input
              className={input}
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </Field>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <Button type="submit" disabled={busy}>
          Save changes
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

const STATUS_STYLE: Record<string, string> = {
  planned: "text-neutral-500",
  ready: "text-neutral-900",
  invoiced: "text-blue-700",
  paid: "text-green-700",
  void: "text-neutral-400 line-through",
};

const PROJECT_STATUSES = ["draft", "active", "on_hold", "completed", "cancelled"] as const;

const PROJECT_STATUS_DOT: Record<string, string> = {
  draft: "bg-neutral-400",
  active: "bg-green-600",
  on_hold: "bg-amber-600",
  completed: "bg-blue-600",
  cancelled: "bg-neutral-300",
};

// The pill style for whichever status is current in the timeline — the rest
// stay a plain muted chip so the one that matters is the one that stands out.
const PROJECT_STATUS_BADGE: Record<string, string> = {
  draft: "bg-neutral-100 text-neutral-700 ring-neutral-300",
  active: "bg-green-50 text-green-700 ring-green-300",
  on_hold: "bg-amber-50 text-amber-700 ring-amber-300",
  completed: "bg-blue-50 text-blue-700 ring-blue-300",
  cancelled: "bg-neutral-100 text-neutral-400 ring-neutral-300 line-through",
};

// What the "+" button proposes before anyone touches the dropdown — the
// status a job most often moves to from where it is now. Still just a
// starting point: the select underneath can be changed to anything.
const NEXT_PROJECT_STATUS: Record<string, (typeof PROJECT_STATUSES)[number]> = {
  draft: "active",
  active: "completed",
  on_hold: "active",
  completed: "completed",
  cancelled: "cancelled",
};

function StatusArrow() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="mx-1 shrink-0 text-neutral-300 sm:mx-2"
      role="presentation"
      aria-hidden
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * One entry in the status timeline — just the badge. Hovering it shows the
 * reason and every reply in a styled card (`HoverCard`, portaled to
 * `document.body` so a scrolling ancestor can't clip it) and the badge
 * scales up slightly to read as clickable. Clicking it opens the reply
 * thread for that entry inline, below the whole timeline, to actually add
 * one — see `StatusDiscussion`.
 */
function StatusEventBadge({
  event,
  isCurrent,
  isOpen,
  onToggle,
}: {
  event: ProjectStatusEvent;
  isCurrent: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <HoverCard
      trigger={
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={isOpen}
          className={`flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition-all duration-150 hover:scale-105 ${
            isCurrent
              ? `${PROJECT_STATUS_BADGE[event.toStatus] ?? "bg-neutral-100 text-neutral-700 ring-neutral-300"} shadow-sm`
              : "bg-white text-neutral-500 ring-neutral-200 hover:bg-neutral-50"
          } ${isOpen ? "outline outline-2 outline-offset-2 outline-accent-500" : ""}`}
        >
          <span
            className={`h-2 w-2 shrink-0 rounded-full ${PROJECT_STATUS_DOT[event.toStatus] ?? "bg-neutral-300"}`}
          />
          {event.toStatus.replace(/_/g, " ")}
        </button>
      }
    >
      <p className="font-medium text-neutral-700">
        {event.actorName || "—"} · {new Date(event.createdAt).toLocaleString()}
      </p>
      <p className="mt-1 text-neutral-600">
        {event.comment || <span className="italic text-neutral-400">No comment</span>}
      </p>
      {event.comments.length > 0 && (
        <div className="mt-2 space-y-2 border-t border-neutral-100 pt-2">
          {event.comments.map((cm) => (
            <div key={cm.id}>
              <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-neutral-500">
                <span className="font-medium text-neutral-700">{cm.authorName}</span>
                {cm.authorType === "client" && (
                  <span className="rounded-full bg-accent-50 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-accent-700">
                    client
                  </span>
                )}
              </div>
              <p className="whitespace-pre-wrap text-neutral-800">{cm.body}</p>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] italic text-neutral-400">Click to reply</p>
    </HoverCard>
  );
}

/** The reason, the reply thread, and the box to add another reply — for whichever badge was clicked. */
function StatusDiscussion({
  slug,
  projectId,
  event,
}: {
  slug: string;
  projectId: string;
  event: ProjectStatusEvent;
}) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setError(null);
    setBusy(true);
    try {
      await unwrap(
        await api.api.c[":slug"].projects[":id"]["status-events"][":eventId"].comments.$post({
          param: { slug, id: projectId, eventId: event.id },
          json: { body: text },
        }),
      );
      setBody("");
      qc.invalidateQueries({ queryKey: ["project-status-events", slug, projectId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-neutral-200 pt-4">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${PROJECT_STATUS_DOT[event.toStatus] ?? "bg-neutral-300"}`}
        />
        <span className="font-medium text-neutral-800">{event.toStatus.replace(/_/g, " ")}</span>
        <span className="text-neutral-300">·</span>
        <span className="text-neutral-500">
          {event.actorName || "—"} · {new Date(event.createdAt).toLocaleString()}
        </span>
      </div>

      {event.comment && (
        <p className="mb-4 max-w-prose text-sm text-neutral-700">{event.comment}</p>
      )}

      {event.comments.length === 0 ? (
        <Empty title="No replies yet">
          Nothing here yet — the client can reply from their own portal too.
        </Empty>
      ) : (
        <div className="mb-4 space-y-2">
          {event.comments.map((cm) => (
            <div
              key={cm.id}
              className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm"
            >
              <div className="mb-1 flex items-center gap-2 text-xs text-neutral-500">
                <span className="font-medium text-neutral-700">{cm.authorName}</span>
                {cm.authorType === "client" && (
                  <span className="rounded-full bg-accent-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-accent-700">
                    client
                  </span>
                )}
                <span>{new Date(cm.createdAt).toLocaleString()}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-neutral-800">{cm.body}</p>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="flex items-end gap-2">
        <textarea
          className={input}
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Reply to the client, or leave a note…"
        />
        <Button type="submit" disabled={busy || !body.trim()}>
          {busy ? "Posting…" : "Post"}
        </Button>
      </form>
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}

/**
 * Invoices and bills have no section of their own: a document only exists
 * because a scheduled line was billed, so it belongs on the line that produced
 * it. Settling it happens there too, next to the amount it settles.
 */
const SECTIONS = [
  ["overview", "Overview"],
  ["schedule", "Draw schedule"],
  ["change-orders", "Change orders"],
  ["contractors", "Contractors"],
  ["expenses", "Expenses"],
  ["commissions", "Commissions"],
] as const;

type SectionId = (typeof SECTIONS)[number][0];

export function ProjectDetail({
  slug,
  projectId,
  role,
  section,
  onSection,
  onBack,
  onOpenContract,
}: {
  slug: string;
  projectId: string;
  role: string;
  section: string | null;
  onSection: (section: string) => void;
  onBack: () => void;
  onOpenContract?: (id: string) => void;
}) {
  const del = useDeleteRecord(slug);
  const canDelete = role === "owner";
  // An unknown section in the URL lands on the overview rather than a blank
  // page — the same forgiveness the company tabs already give.
  const active: SectionId = SECTIONS.find(([id]) => id === section)?.[0] ?? "overview";
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [openCommitment, setOpenCommitment] = useState<string | null>(null);
  // The milestone or draw whose payment form is open. Ids are unique across
  // both, and only one section is ever on screen, so one piece of state does.
  const [settling, setSettling] = useState<string | null>(null);
  // Milestones, change orders, and draws whose payment history is expanded —
  // same reasoning as `settling`: one set covers every row on screen because
  // only one section is ever visible at a time.
  const [openPayments, setOpenPayments] = useState<Set<string>>(new Set());
  const togglePayments = (id: string) =>
    setOpenPayments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Change orders the operator has *unticked*. Held as the exclusions so the
  // default — everything pending rides along — needs no priming from a query
  // that hasn't landed yet, and a newly approved one joins in automatically.
  const [heldBack, setHeldBack] = useState<Set<string>>(new Set());
  const [showCommitment, setShowCommitment] = useState(false);
  /**
   * Adding and editing are two states, not one: the "Record…" button opens a
   * form under the panel's own heading, while editing one row opens a dialog
   * over the table. Sharing a single piece of state made the button read
   * "Cancel" for a form it hadn't opened.
   */
  const [addingExpense, setAddingExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ProjectExpenseRow | null>(null);
  const [addingCommission, setAddingCommission] = useState(false);
  const [editingCommission, setEditingCommission] = useState<ProjectCommissionRow | null>(null);
  const [editingCommitment, setEditingCommitment] = useState<string | null>(null);
  const [editSchedule, setEditSchedule] = useState(false);
  // Empty until touched, so the select tracks the project's live status
  // rather than freezing on whatever it was when the page opened.
  const [newStatus, setNewStatus] = useState<"" | (typeof PROJECT_STATUSES)[number]>("");
  const [statusComment, setStatusComment] = useState("");
  const [showStatusForm, setShowStatusForm] = useState(false);
  // Which timeline entry's reply thread is open — null until a badge is
  // clicked, and toggled shut by clicking the same one again.
  const [openEventId, setOpenEventId] = useState<string | null>(null);

  /* ---- reads: scoped to this project, and to the section on screen ---- */

  // The schedule and the project list are the page's own furniture — the title,
  // the client, the contract value — so they load whichever section is open.
  const schedule = useQuery({
    queryKey: ["schedule", slug, projectId],
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].billing[":projectId"].schedule.$get({
          param: { slug, projectId },
        }),
      ),
  });

  // Only for the client's name and the project code, which the schedule
  // response doesn't carry.
  const projects = useQuery({
    queryKey: ["projects", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].projects.$get({ param: { slug } })),
  });

  // The rest wait for the section that shows them. React Query keeps what it
  // has fetched, so coming back to a section is instant.
  const overview = useQuery({
    queryKey: ["reports-overview", slug],
    enabled: active === "overview",
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].reports.overview.$get({ param: { slug }, query: {} })),
  });

  // The status timeline — who moved this job where, and why. Only the
  // overview shows it, so it fetches only when that's the tab on screen.
  const statusEvents = useQuery({
    queryKey: ["project-status-events", slug, projectId],
    enabled: active === "overview",
    queryFn: () => fetchProjectStatusEvents(slug, projectId),
  });

  // Also on the schedule: a change order moves the contract, so it belongs in
  // the picture of what will be billed, not only on its own page.
  const changeOrders = useQuery({
    queryKey: ["change-orders", slug, projectId],
    enabled: active === "change-orders" || active === "schedule",
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].billing[":projectId"]["change-orders"].$get({
          param: { slug, projectId },
        }),
      ),
  });

  const commitments = useQuery({
    queryKey: ["commitments", slug, projectId],
    enabled: active === "contractors",
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].payables.commitments.$get({
          param: { slug },
          query: { projectId },
        }),
      ),
  });

  const draws = useQuery({
    queryKey: ["draws", slug, openCommitment],
    enabled: !!openCommitment,
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].payables.commitments[":id"].draws.$get({
          param: { slug, id: openCommitment! },
        }),
      ),
  });

  // Picker options, fetched only for the sections whose forms offer them.
  const contractors = useQuery({
    queryKey: ["contractors", slug],
    enabled: active === "contractors" || active === "commissions",
    queryFn: async () => unwrap(await api.api.c[":slug"].contractors.$get({ param: { slug } })),
  });
  const clients = useQuery({
    queryKey: ["clients", slug],
    enabled: active === "commissions",
    queryFn: async () => unwrap(await api.api.c[":slug"].clients.$get({ param: { slug } })),
  });
  const costCodes = useQuery({
    queryKey: ["cost-codes", slug],
    enabled: active === "contractors" || active === "expenses",
    queryFn: async () => unwrap(await api.api.c[":slug"]["cost-codes"].$get({ param: { slug } })),
  });

  const expenses = useResource({
    key: ["expenses", slug, projectId],
    enabled: active === "expenses",
    list: () => listProjectExpenses(slug, projectId),
    create: async (body: Record<string, unknown>) =>
      unwrap(await api.api.c[":slug"].expenses.$post({ param: { slug }, json: body as never })),
    update: async (id: string, body: Record<string, unknown>) =>
      unwrap(
        await api.api.c[":slug"].expenses[":id"].$patch({
          param: { slug, id },
          json: body as never,
        }),
      ),
    alsoInvalidate: [
      ["expenses", slug],
      ["expenses-by-cost-code", slug],
      ["reports-overview", slug],
    ],
  });

  const commissions = useResource({
    key: ["commissions", slug, projectId],
    enabled: active === "commissions",
    list: () => listProjectCommissions(slug, projectId),
    create: async (body: Record<string, unknown>) =>
      unwrap(await api.api.c[":slug"].commissions.$post({ param: { slug }, json: body as never })),
    update: async (id: string, body: Record<string, unknown>) =>
      unwrap(
        await api.api.c[":slug"].commissions[":id"].$patch({
          param: { slug, id },
          json: body as never,
        }),
      ),
    alsoInvalidate: [
      ["commissions", slug],
      ["commissions-summary", slug],
      ["reports-overview", slug],
    ],
  });

  /**
   * Refresh everything a write here can touch. Keys are prefixes, so
   * `["expenses", slug]` also clears the `["expenses", slug, projectId]` this
   * page reads — the company-wide tab and this one can't disagree afterwards.
   */
  function refresh() {
    for (const k of [
      "schedule",
      "change-orders",
      "invoices",
      "commitments",
      "commitment-earnings",
      "bills",
      "draws",
      "expenses",
      "commissions",
      "payments",
      "cost-summary",
      "reports-overview",
      "projects",
      "project-status-events",
    ]) {
      qc.invalidateQueries({ queryKey: [k, slug] });
    }
  }

  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Something went wrong. Try again."),
  });

  /* ---- tables ---- */

  const milestonesTable = useTable([...(schedule.data?.schedule.milestones ?? [])], {
    filter: (m, q) => `${m.name} ${m.status}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      seq: (m) => m.seq,
      milestone: (m) => m.name.toLowerCase(),
      status: (m) => m.status,
      amount: (m) => Number(m.amount),
    },
  });

  const commitmentsTable = useTable(commitments.data?.commitments ?? [], {
    filter: (cm, q) =>
      `${cm.contractorName} ${cm.scope ?? ""} ${cm.costCode ?? ""}`
        .toLowerCase()
        .includes(q.toLowerCase()),
    sorters: {
      contractor: (cm) => cm.contractorName.toLowerCase(),
      scope: (cm) => (cm.scope ?? "").toLowerCase(),
      costCode: (cm) => (cm.costCode ?? "").toLowerCase(),
      value: (cm) => Number(cm.contractValue),
      billed: (cm) => Number(cm.billed),
      stillToCome: (cm) => Number(sub(cm.contractValue, cm.billed)),
      outstanding: (cm) => Number(cm.outstanding),
    },
  });

  const drawsTable = useTable([...(draws.data?.schedule.milestones ?? [])], {
    filter: (d, q) =>
      `${d.name} ${d.frozen ? "billed" : d.status}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      seq: (d) => d.seq,
      draw: (d) => d.name.toLowerCase(),
      status: (d) => (d.frozen ? "billed" : d.status),
      amount: (d) => Number(d.amount),
    },
  });

  const expensesTable = useTable(expenses.query.data?.expenses ?? [], {
    filter: (e, q) =>
      `${e.description} ${e.costCode} ${e.costCodeName}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      date: (e) => e.incurredOn,
      description: (e) => e.description.toLowerCase(),
      costCode: (e) => e.costCode.toLowerCase(),
      amount: (e) => Number(e.amount),
    },
  });

  const commissionsTable = useTable(commissions.query.data?.commissions ?? [], {
    filter: (cm, q) =>
      `${cm.description} ${cm.clientName ?? ""} ${cm.contractorName ?? ""} ${cm.reference ?? ""}`
        .toLowerCase()
        .includes(q.toLowerCase()),
    sorters: {
      date: (cm) => cm.earnedOn,
      description: (cm) => cm.description.toLowerCase(),
      from: (cm) => (cm.clientName ?? cm.contractorName ?? "").toLowerCase(),
      amount: (cm) => Number(cm.amount),
    },
  });

  if (schedule.isPending) return <Loading />;
  if (schedule.isError) {
    return (
      <>
        <ErrorNote>{(schedule.error as Error).message}</ErrorNote>
      </>
    );
  }

  const project = schedule.data.project;
  const sched = schedule.data.schedule;
  // Milestone id → the invoice it was billed on; draw id → its bill.
  const settlements = schedule.data.settlements;
  const cos = schedule.data.changeOrders;
  /** The pending change orders still ticked — what the next invoice will carry. */
  const riding = cos.pending.filter((id) => !heldBack.has(id));

  const openStatusEvent = statusEvents.data?.events.find((ev) => ev.id === openEventId);

  /** Settling a client invoice, wherever on this page it is being settled from. */
  const recordInvoicePayment = (inv: Settlement, p: SettlementPayment) =>
    api.api.c[":slug"].payments.$post({
      param: { slug },
      json: {
        clientId: inv.partyId,
        receivedOn: p.on,
        amount: p.amount,
        currency: ccy,
        ...(p.method ? { method: p.method } : {}),
        ...(p.reference ? { reference: p.reference } : {}),
        applications: [{ invoiceId: inv.id, amount: p.amount }],
      },
    });

  /**
   * Change orders with no schedule line of their own.
   *
   * Only an approved `own_milestone` change order becomes a milestone; the
   * other modes reach an invoice without one, and a change order that nobody
   * has approved yet does nothing at all. All of them still belong on this
   * page — a change order you can't see on the schedule is one you forget is
   * coming.
   */
  const looseChangeOrders = (changeOrders.data?.changeOrders ?? []).filter(
    (co) =>
      co.status !== "void" && !(co.status === "approved" && co.billingMode === "own_milestone"),
  );
  const drawSettlements = draws.data?.settlements ?? {};
  // A commitment can be in a currency of its own, so its bills settle in that.
  const drawCurrency = draws.data?.commitment.currency ?? project.currency;
  // The row backing the open commitment — its own query only has the id, not
  // the contractor's name, and that's what makes the panel below identifiable.
  const openCommitmentRow = commitments.data?.commitments.find((cm) => cm.id === openCommitment);
  const listRow = projects.data?.projects.find((p) => p.id === projectId);
  const figures = overview.data?.projects.find((r) => r.projectId === projectId);
  const ccy = project.currency;

  // Profit, unlike the margins above, counts the whole contract regardless of
  // what's been invoiced and the whole committed cost regardless of what's
  // been billed — `sched.revisedContractValue` already has this project's
  // approved change orders folded in, so it doesn't need a trip through
  // reports.overview the way the company-wide profit card does.
  const profit = sub(
    sub(
      add(sched.revisedContractValue, figures?.commissionEarned ?? ZERO),
      figures?.directCost ?? ZERO,
    ),
    figures?.committed ?? ZERO,
  );

  return (
    <>
      {/* The project's own title lives in its nav rather than a header above
          it — one pinned block, not two, so a long table only has one bar to
          scroll underneath. */}
      <nav className="sticky top-14 z-20 -mx-6 mb-4 border-b border-neutral-200 bg-neutral-50/90 px-6 backdrop-blur-md">
        <div className="flex items-start justify-between gap-4 pt-3">
          <div>
            <h1 className="text-base font-semibold tracking-tight">
              <span className="tabular text-neutral-500">{project.code}</span> {project.name}
            </h1>
            <p className="text-xs text-neutral-500">
              {listRow?.clientName ?? "—"} · {project.status.replace(/_/g, " ")} · retainage{" "}
              {formatPercent(project.retainagePct)}
              {listRow?.sourceContractId && (
                <>
                  {" · "}
                  {onOpenContract ? (
                    <button
                      type="button"
                      onClick={() => onOpenContract(listRow.sourceContractId!)}
                      className="underline underline-offset-2 hover:text-neutral-900"
                    >
                      From contract {listRow.sourceContractNumber}
                    </button>
                  ) : (
                    <>From contract {listRow.sourceContractNumber}</>
                  )}
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onBack}
            className="mt-0.5 whitespace-nowrap text-sm text-neutral-500 transition-colors hover:text-neutral-900"
          >
            ← All projects
          </button>
        </div>
        <div className="flex flex-wrap gap-x-1">
          {SECTIONS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => onSection(id)}
              aria-current={active === id ? "page" : undefined}
              className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors ${
                active === id
                  ? "border-accent-500 font-medium text-neutral-900"
                  : "border-transparent text-neutral-500 hover:text-neutral-900"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </nav>

      {error && <ErrorNote>{error}</ErrorNote>}
      {del.error && <ErrorNote>{del.error}</ErrorNote>}

      <Section id="overview" active={active}>
        <Panel title="Status">
          {statusEvents.isPending ? (
            <Loading />
          ) : (
            <div className="flex flex-wrap items-center gap-y-3 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
              {statusEvents.data?.events.length ? (
                [...statusEvents.data.events].reverse().map((ev, i, arr) => {
                  const isCurrent = i === arr.length - 1;
                  return (
                    <Fragment key={ev.id}>
                      {i > 0 && <StatusArrow />}
                      <StatusEventBadge
                        event={ev}
                        isCurrent={isCurrent}
                        isOpen={ev.id === openEventId}
                        onToggle={() => setOpenEventId((cur) => (cur === ev.id ? null : ev.id))}
                      />
                    </Fragment>
                  );
                })
              ) : (
                <div
                  className={`flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ring-1 shadow-sm ${
                    PROJECT_STATUS_BADGE[project.status] ??
                    "bg-neutral-100 text-neutral-700 ring-neutral-300"
                  }`}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${PROJECT_STATUS_DOT[project.status] ?? "bg-neutral-300"}`}
                  />
                  {project.status.replace(/_/g, " ")}
                </div>
              )}

              {/* Sits right after the current status, so the action and the
                  thing it acts on read as one unit rather than a header button
                  pointing down at a badge row it's separated from. */}
              <button
                type="button"
                onClick={() => {
                  // Default the picker to where this status usually goes next,
                  // rather than leaving it parked on the current value.
                  setNewStatus(
                    NEXT_PROJECT_STATUS[project.status] ??
                      (project.status as (typeof PROJECT_STATUSES)[number]),
                  );
                  setShowStatusForm(true);
                }}
                aria-label="Add status update"
                className="ml-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 shadow-sm transition-colors hover:border-accent-500 hover:text-accent-600"
              >
                +
              </button>
            </div>
          )}

          {showStatusForm && (
            <Modal title="Update status" onClose={() => setShowStatusForm(false)}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const status = newStatus || project.status;
                  run.mutate(() =>
                    unwrap(
                      api.api.c[":slug"].projects[":id"].status.$post({
                        param: { slug, id: projectId },
                        json: {
                          status,
                          ...(statusComment.trim() ? { comment: statusComment.trim() } : {}),
                        },
                      }),
                    ),
                  );
                  setNewStatus("");
                  setStatusComment("");
                  setShowStatusForm(false);
                }}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Status">
                    <select
                      className={input}
                      value={newStatus || project.status}
                      onChange={(e) =>
                        setNewStatus(e.target.value as (typeof PROJECT_STATUSES)[number])
                      }
                    >
                      {PROJECT_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Comment" hint="Optional — shown on hover in the timeline">
                    <input
                      className={input}
                      value={statusComment}
                      onChange={(e) => setStatusComment(e.target.value)}
                      placeholder="Why this changed, or what's next"
                    />
                  </Field>
                </div>
                <div className="mt-3">
                  <Button
                    type="submit"
                    disabled={run.isPending || (!newStatus && !statusComment.trim())}
                  >
                    {run.isPending ? "Saving…" : "Update status"}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {openStatusEvent && (
            <StatusDiscussion slug={slug} projectId={projectId} event={openStatusEvent} />
          )}
        </Panel>

        <Panel title="Where this job stands">
          {/* A project with no ledger activity yet has no row in the overview,
              and zeroes are the honest answer for it — but only once the
              figures have actually arrived. */}
          {overview.isPending ? (
            <Loading />
          ) : (
            <div className="mb-6 grid gap-3 sm:grid-cols-3">
              <Stat
                label="Contract"
                value={fmt(sched.revisedContractValue)}
                currency={ccy}
                hint="Original, plus approved change orders"
              />
              <Stat
                label="Change orders"
                value={fmt(cos.approvedTotal)}
                currency={ccy}
                hint={
                  cos.awaiting > 0
                    ? `${cos.awaiting} awaiting approval, not counted`
                    : `${cos.approved} approved`
                }
              />
              <Stat label="Invoiced" value={fmt(figures?.invoiced ?? "0.00")} currency={ccy} />
              <Stat
                label="Outstanding"
                value={fmt(figures?.ar ?? "0.00")}
                currency={ccy}
                hint="Invoiced, not yet paid"
              />
              <Stat
                label="Commissions"
                value={fmt(figures?.commissionEarned ?? "0.00")}
                currency={ccy}
                hint="Earned outside the contract"
              />
              <Stat
                label="Committed"
                value={fmt(figures?.committed ?? "0.00")}
                currency={ccy}
                hint="Still to come, once billed work is taken out"
              />
              <Stat
                label="Direct cost"
                value={fmt(figures?.directCost ?? "0.00")}
                currency={ccy}
                hint="Billed to the job so far"
              />
              <Stat
                label="Net margin"
                value={fmt(figures?.netMargin ?? "0.00")}
                currency={ccy}
                hint="After this job's share of overhead"
              />
              <Stat
                label="Profit"
                value={fmt(profit)}
                currency={ccy}
                hint="Full contract value, regardless of what's billed"
                emphasis
              />
            </div>
          )}

          <p className="max-w-prose text-sm text-neutral-500">
            Every figure here is summed from the ledger, over the same rows the sections behind
            these tabs list — so the headline and the detail cannot disagree. Change orders are
            already inside the contract figure once approved, and count for nothing before that.
            Commissions are revenue this job earned outside the contract. Committed is cost still to
            come — what contractors are owed under signed commitments that they have not billed yet;
            a draw they do bill leaves committed and lands in direct cost, so the two never count
            the same money twice. Net margin is gross less this job's share of company overhead.
            Profit is a different question from either margin: it counts the whole contract and the
            whole committed cost, whether or not either side has actually been invoiced or billed
            yet — what the job is worth outright.
          </p>
        </Panel>
      </Section>

      {/* ---- what the client will be invoiced ---- */}
      <Section id="schedule" active={active}>
        <Panel
          title="Draw schedule"
          action={
            sched.milestones.length > 0 && !editSchedule ? (
              <Button variant="ghost" onClick={() => setEditSchedule(true)}>
                Replace schedule
              </Button>
            ) : editSchedule ? (
              <Button variant="ghost" onClick={() => setEditSchedule(false)}>
                Cancel
              </Button>
            ) : undefined
          }
        >
          {sched.milestones.length === 0 || editSchedule ? (
            <ScheduleRowsForm
              intro="Split the contract into the payments you'll invoice. Percentages resolve against the contract value, and the last one absorbs the rounding so they always sum exactly. Anything already invoiced is frozen, so this is refused once billing has started — add a change order instead."
              addLabel="Add milestone"
              submitLabel="Save schedule"
              defaultRows={[
                { name: "Deposit", percent: "50" },
                { name: "Progress", percent: "40" },
                { name: "Final", percent: "10" },
              ]}
              busy={run.isPending}
              onSubmit={(rows) => {
                run.mutate(() =>
                  unwrap(
                    api.api.c[":slug"].billing[":projectId"].schedule.$put({
                      param: { slug, projectId },
                      json: { milestones: rows },
                    }),
                  ),
                );
                setEditSchedule(false);
              }}
            />
          ) : (
            <>
              <TableSearch
                value={milestonesTable.query}
                onChange={milestonesTable.setQuery}
                placeholder="Filter milestones…"
              />
              <Table
                head={
                  <>
                    <SortTh table={milestonesTable} sortKey="seq">
                      #
                    </SortTh>
                    <SortTh table={milestonesTable} sortKey="milestone">
                      Milestone
                    </SortTh>
                    <SortTh table={milestonesTable} sortKey="status">
                      Status
                    </SortTh>
                    <SortTh table={milestonesTable} sortKey="amount" right>
                      Amount
                    </SortTh>
                    <Th>Invoice</Th>
                    <Th right>Outstanding</Th>
                    <Th />
                  </>
                }
              >
                {milestonesTable.rows.map((m) => {
                  const inv = settlements[m.id];
                  return (
                    <Fragment key={m.id}>
                      <tr className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
                        <Td mono muted>
                          {m.seq}
                        </Td>
                        <Td>
                          {m.name}
                          {m.source === "change_order" && (
                            <span className="ml-2 rounded-full border border-neutral-200 bg-neutral-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
                              change order
                            </span>
                          )}
                        </Td>
                        <Td>
                          <span className={STATUS_STYLE[m.status] ?? ""}>{m.status}</span>
                          {m.frozen && (
                            <span className="ml-2 text-xs text-neutral-500">frozen</span>
                          )}
                        </Td>
                        <Td right mono>
                          {fmt(m.amount)}
                        </Td>
                        <Td mono muted>
                          {inv?.number ?? "—"}
                          {inv?.overdue && (
                            <span className="ml-2 text-xs text-red-600">overdue</span>
                          )}
                        </Td>
                        <Td right mono>
                          {inv ? fmt(inv.outstanding) : "—"}
                        </Td>
                        <Td right>
                          {/* Invoice it, then settle it — the two things you
                              can do to a scheduled line, in that order. */}
                          <div className="flex items-center justify-end gap-1">
                            {inv && (
                              <ChevronButton
                                open={openPayments.has(m.id)}
                                onClick={() => togglePayments(m.id)}
                                label="payments"
                              />
                            )}
                            {!m.frozen ? (
                              <Button
                                variant="ghost"
                                disabled={run.isPending}
                                onClick={() =>
                                  run.mutate(() =>
                                    unwrap(
                                      api.api.c[":slug"].invoices.$post({
                                        param: { slug },
                                        json: {
                                          projectId,
                                          milestoneId: m.id,
                                          // Explicit, so an unticked change
                                          // order is genuinely held back rather
                                          // than swept along by the default.
                                          changeOrderIds: riding,
                                        },
                                      }),
                                    ),
                                  )
                                }
                              >
                                Invoice
                              </Button>
                            ) : (
                              inv &&
                              inv.outstanding !== "0.00" && (
                                <Button
                                  variant="ghost"
                                  onClick={() => setSettling(settling === m.id ? null : m.id)}
                                >
                                  {settling === m.id ? "Cancel" : "Record payment"}
                                </Button>
                              )
                            )}
                          </div>
                        </Td>
                      </tr>
                      {openPayments.has(m.id) && inv && (
                        <tr>
                          <td colSpan={7} className="bg-white px-4 py-3">
                            <PaymentsList slug={slug} kind="invoice" id={inv.id} currency={ccy} />
                          </td>
                        </tr>
                      )}
                      {settling === m.id && inv && (
                        <tr>
                          <td colSpan={7} className="bg-neutral-50 px-4 py-4">
                            <RecordPaymentForm
                              settlement={inv}
                              currency={ccy}
                              noun="invoice"
                              dateLabel="Received on"
                              busy={run.isPending}
                              onSubmit={(p) => {
                                run.mutate(() => unwrap(recordInvoicePayment(inv, p)));
                                setSettling(null);
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                <tr className="border-t-2 border-neutral-900 font-medium">
                  <Td />
                  <Td>Scheduled total</Td>
                  <Td />
                  <Td right mono>
                    {fmt(sched.scheduledTotal)}
                  </Td>
                  <Td />
                  <Td />
                  <Td />
                </tr>

                {/* Extra work that has no milestone to sit on. Listed under
                    the schedule because that is where you look to find out
                    what is still to be billed. */}
                {looseChangeOrders.length > 0 && (
                  <tr className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
                    <td
                      colSpan={7}
                      className="pt-5 pb-1 text-xs font-medium uppercase tracking-wide text-neutral-500"
                    >
                      Change orders
                    </td>
                  </tr>
                )}
                {looseChangeOrders.map((co) => {
                  const inv = cos.settlements[co.id];
                  const canRide = cos.pending.includes(co.id);
                  return (
                    <Fragment key={co.id}>
                      <tr className="border-b border-neutral-100 last:border-0 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
                        <Td mono muted>
                          CO {co.number}
                        </Td>
                        <Td>
                          {/* Ticked rides on the next invoice; cleared holds it
                            back, still pending, for the one after. */}
                          {canRide ? (
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={!heldBack.has(co.id)}
                                onChange={() =>
                                  setHeldBack((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(co.id)) next.delete(co.id);
                                    else next.add(co.id);
                                    return next;
                                  })
                                }
                              />
                              {co.title}
                            </label>
                          ) : (
                            co.title
                          )}
                        </Td>
                        <Td>
                          <ChangeOrderStatus status={co.status} settlement={inv} />
                        </Td>
                        <Td right mono>
                          {fmt(co.amount)}
                        </Td>
                        <Td mono muted>
                          {inv?.number ?? "—"}
                          {inv?.overdue && (
                            <span className="ml-2 text-xs text-red-600">overdue</span>
                          )}
                        </Td>
                        <Td right mono>
                          {inv ? fmt(inv.outstanding) : "—"}
                        </Td>
                        <Td right>
                          {/* Invoice it on its own, then settle it — the same two
                            steps a milestone goes through, in its own row. */}
                          <div className="flex items-center justify-end gap-1">
                            {inv && (
                              <ChevronButton
                                open={openPayments.has(co.id)}
                                onClick={() => togglePayments(co.id)}
                                label="payments"
                              />
                            )}
                            <ChangeOrderAction
                              settlement={inv}
                              canInvoice={canRide}
                              busy={run.isPending}
                              payOpen={settling === co.id}
                              onInvoice={() =>
                                run.mutate(() =>
                                  unwrap(
                                    api.api.c[":slug"].invoices.$post({
                                      param: { slug },
                                      json: {
                                        projectId,
                                        milestoneId: null,
                                        changeOrderIds: [co.id],
                                      },
                                    }),
                                  ),
                                )
                              }
                              onTogglePay={() => setSettling(settling === co.id ? null : co.id)}
                            />
                          </div>
                        </Td>
                      </tr>
                      {openPayments.has(co.id) && inv && (
                        <tr>
                          <td colSpan={7} className="bg-white px-4 py-3">
                            <PaymentsList slug={slug} kind="invoice" id={inv.id} currency={ccy} />
                          </td>
                        </tr>
                      )}
                      {settling === co.id && inv && (
                        <tr>
                          <td colSpan={7} className="bg-neutral-50 px-4 py-4">
                            <RecordPaymentForm
                              settlement={inv}
                              currency={ccy}
                              noun="invoice"
                              dateLabel="Received on"
                              busy={run.isPending}
                              onSubmit={(p) => {
                                run.mutate(() => unwrap(recordInvoicePayment(inv, p)));
                                setSettling(null);
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </Table>

              {looseChangeOrders.length > 0 && (
                <p className="mt-3 max-w-prose text-xs text-neutral-500">
                  Ticked change orders ride along on the next milestone you invoice; unticked ones
                  stay pending. Either way, one can be invoiced on its own from its row — the only
                  way to bill extra work on a job whose schedule is fully invoiced. One that hasn't
                  been approved yet has no action, and doesn't move the contract.
                </p>
              )}

              {sched.unallocated !== "0.00" && (
                <p className="mt-2 text-xs text-amber-700">
                  {fmt(sched.unallocated, ccy)} of the contract has no open milestone to absorb it.
                </p>
              )}
            </>
          )}
        </Panel>
      </Section>

      <Section id="change-orders" active={active}>
        <ChangeOrders
          rows={changeOrders.data?.changeOrders ?? []}
          pending={changeOrders.isPending}
          busy={run.isPending}
          currency={ccy}
          settlements={cos.settlements}
          pendingIds={cos.pending}
          onInvoice={(id) =>
            run.mutate(() =>
              unwrap(
                api.api.c[":slug"].invoices.$post({
                  param: { slug },
                  json: { projectId, milestoneId: null, changeOrderIds: [id] },
                }),
              ),
            )
          }
          onRecordPayment={(inv, p) => run.mutate(() => unwrap(recordInvoicePayment(inv, p)))}
          onCreate={(body) =>
            run.mutate(() =>
              unwrap(
                api.api.c[":slug"].billing[":projectId"]["change-orders"].$post({
                  param: { slug, projectId },
                  json: body,
                }),
              ),
            )
          }
          onApprove={(id) =>
            run.mutate(() =>
              unwrap(
                api.api.c[":slug"].billing[":projectId"]["change-orders"][":id"].$patch({
                  param: { slug, projectId, id },
                  json: { status: "approved" },
                }),
              ),
            )
          }
          {...(canDelete && { onDelete: (id: string) => del.remove("change_order", id) })}
        />
      </Section>

      {/* ---- what the project owes out ---- */}
      <Section id="contractors" active={active}>
        <Panel
          title="Contractors"
          action={<Button onClick={() => setShowCommitment(true)}>Add contractor</Button>}
        >
          <p className="mb-3 max-w-prose text-sm text-neutral-500">
            A commitment is what you've agreed to pay one contractor here. It counts against this
            job from the moment you sign, not when the first bill arrives — which is what makes
            cost-to-come a real number.
          </p>

          {showCommitment && (
            <Modal title="Add contractor" wide onClose={() => setShowCommitment(false)}>
              <CommitmentForm
                slug={slug}
                currency={ccy}
                projects={[]}
                lockedProjectId={projectId}
                lockedProjectValue={project.originalContractValue}
                costCodes={costCodes.data?.costCodes ?? []}
                bare
                busy={run.isPending}
                onSubmit={(body) => {
                  run.mutate(() =>
                    unwrap(
                      api.api.c[":slug"].payables.commitments.$post({
                        param: { slug },
                        json: body,
                      }),
                    ),
                  );
                  setShowCommitment(false);
                }}
              />
            </Modal>
          )}

          {commitments.isPending ? (
            <Loading />
          ) : !commitments.data?.commitments.length ? (
            <Empty title="No contractors on this project yet">
              {contractors.data?.contractors.length
                ? "Commit one to a scope of work above."
                : "Add a contractor to the company first."}
            </Empty>
          ) : (
            <>
              <TableSearch
                value={commitmentsTable.query}
                onChange={commitmentsTable.setQuery}
                placeholder="Filter contractors…"
              />
              <Table
                head={
                  <>
                    <SortTh table={commitmentsTable} sortKey="contractor">
                      Contractor
                    </SortTh>
                    <SortTh table={commitmentsTable} sortKey="scope">
                      Scope
                    </SortTh>
                    <SortTh table={commitmentsTable} sortKey="costCode">
                      Cost code
                    </SortTh>
                    <SortTh table={commitmentsTable} sortKey="value" right>
                      Committed
                    </SortTh>
                    <SortTh table={commitmentsTable} sortKey="billed" right>
                      Billed
                    </SortTh>
                    <SortTh table={commitmentsTable} sortKey="stillToCome" right>
                      Still to come
                    </SortTh>
                    <SortTh table={commitmentsTable} sortKey="outstanding" right>
                      Owed
                    </SortTh>
                    <Th />
                  </>
                }
              >
                {commitmentsTable.rows.map((cm) => (
                  <tr
                    key={cm.id}
                    className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                  >
                    <Td>{cm.contractorName}</Td>
                    <Td muted>{cm.scope ?? "—"}</Td>
                    <Td mono muted>
                      {cm.costCode ?? "—"}
                    </Td>
                    <Td right mono>
                      {fmt(cm.contractValue)}
                      {/* A share is a derived figure, so the row says what it
                          was derived from rather than presenting it as a
                          number somebody typed. */}
                      {cm.basis === "percent_of_contract" && (
                        <span className="ml-2 text-xs font-normal text-neutral-500">
                          {formatPercent(cm.percent ?? "0")} of {fmt(cm.percentBase)}
                          {cm.deductionTotal !== "0.00" && ` less ${fmt(cm.deductionTotal)}`}
                        </span>
                      )}
                    </Td>
                    <Td right mono muted>
                      {fmt(cm.billed)}
                    </Td>
                    <Td right mono muted>
                      {fmt(sub(cm.contractValue, cm.billed))}
                    </Td>
                    <Td right mono>
                      {fmt(cm.outstanding)}
                    </Td>
                    <Td right>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          onClick={() =>
                            setEditingCommitment(editingCommitment === cm.id ? null : cm.id)
                          }
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => setOpenCommitment(openCommitment === cm.id ? null : cm.id)}
                        >
                          {openCommitment === cm.id ? "Hide" : "Open"}
                        </Button>
                        {canDelete && (
                          <DeleteButton
                            busy={del.deleting}
                            onConfirm={() => {
                              if (openCommitment === cm.id) setOpenCommitment(null);
                              if (editingCommitment === cm.id) setEditingCommitment(null);
                              del.remove("commitment", cm.id);
                            }}
                          />
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            </>
          )}

          {(() => {
            const editing = commitments.data?.commitments.find((cm) => cm.id === editingCommitment);
            if (!editing) return null;
            return (
              <Modal
                title={`Edit — ${editing.contractorName}`}
                wide
                onClose={() => setEditingCommitment(null)}
              >
                <CommitmentEditForm
                  commitment={editing}
                  currency={editing.currency}
                  busy={run.isPending}
                  onCancel={() => setEditingCommitment(null)}
                  onSubmit={(body) => {
                    run.mutate(() =>
                      unwrap(
                        api.api.c[":slug"].payables.commitments[":id"].$patch({
                          param: { slug, id: editing.id },
                          json: body,
                        }),
                      ),
                    );
                    setEditingCommitment(null);
                  }}
                />
              </Modal>
            );
          })()}

          {/* Keyed on the commitment so opening a different contractor starts
              from their stored selection rather than the last one's edits. */}
          {openCommitment && (
            <CommitmentEarnings
              key={openCommitment}
              slug={slug}
              commitmentId={openCommitment}
              contractorName={openCommitmentRow?.contractorName}
              busy={run.isPending}
              onError={setError}
              onChanged={refresh}
            />
          )}

          {openCommitment && (
            <div className="mt-6 rounded-xl border border-neutral-200 bg-neutral-50 p-5">
              <h3 className="mb-3 text-sm font-medium">
                Draw schedule
                {openCommitmentRow && (
                  <span className="font-normal text-neutral-500">
                    {" "}
                    — {openCommitmentRow.contractorName}
                  </span>
                )}
              </h3>
              {draws.isPending ? (
                <Loading />
              ) : !draws.data?.schedule.milestones.length ? (
                <ScheduleRowsForm
                  intro="Split the commitment into the payments you'll make. Percentages resolve against the commitment value, and the last one absorbs the rounding so they always sum exactly."
                  addLabel="Add draw"
                  submitLabel="Save schedule"
                  defaultRows={[
                    { name: "First", percent: "50" },
                    { name: "Progress", percent: "40" },
                    { name: "Final", percent: "10" },
                  ]}
                  busy={run.isPending}
                  onSubmit={(rows) =>
                    run.mutate(() =>
                      unwrap(
                        api.api.c[":slug"].payables.commitments[":id"].draws.$put({
                          param: { slug, id: openCommitment },
                          json: { draws: rows },
                        }),
                      ),
                    )
                  }
                />
              ) : (
                <Table
                  maxHeight={false}
                  head={
                    <>
                      <SortTh table={drawsTable} sortKey="seq">
                        #
                      </SortTh>
                      <SortTh table={drawsTable} sortKey="draw">
                        Draw
                      </SortTh>
                      <SortTh table={drawsTable} sortKey="status">
                        Status
                      </SortTh>
                      <SortTh table={drawsTable} sortKey="amount" right>
                        Amount
                      </SortTh>
                      <Th>Bill</Th>
                      <Th right>Outstanding</Th>
                      <Th />
                    </>
                  }
                >
                  {drawsTable.rows.map((d) => {
                    const bill = drawSettlements[d.id];
                    return (
                      <Fragment key={d.id}>
                        <tr className="border-b border-neutral-100 last:border-0 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
                          <Td mono muted>
                            {d.seq}
                          </Td>
                          <Td>{d.name}</Td>
                          <Td muted>{d.frozen ? "billed" : d.status}</Td>
                          <Td right mono>
                            {fmt(d.amount)}
                          </Td>
                          <Td mono muted>
                            {bill?.number ?? (bill ? "—" : "")}
                            {bill?.overdue && (
                              <span className="ml-2 text-xs text-red-600">overdue</span>
                            )}
                          </Td>
                          <Td right mono>
                            {bill ? fmt(bill.outstanding) : "—"}
                          </Td>
                          <Td right>
                            {/* Bill it, then pay it — the mirror of the client
                                side, run in the opposite direction. */}
                            <div className="flex items-center justify-end gap-1">
                              {bill && (
                                <ChevronButton
                                  open={openPayments.has(d.id)}
                                  onClick={() => togglePayments(d.id)}
                                  label="payments"
                                />
                              )}
                              {!d.frozen ? (
                                <Button
                                  variant="ghost"
                                  disabled={run.isPending}
                                  onClick={() =>
                                    run.mutate(() =>
                                      unwrap(
                                        api.api.c[":slug"].payables.bills.$post({
                                          param: { slug },
                                          json: {
                                            commitmentId: openCommitment,
                                            drawId: d.id,
                                            issueDate: new Date().toISOString().slice(0, 10),
                                          },
                                        }),
                                      ),
                                    )
                                  }
                                >
                                  Bill this draw
                                </Button>
                              ) : (
                                bill &&
                                bill.outstanding !== "0.00" && (
                                  <Button
                                    variant="ghost"
                                    onClick={() => setSettling(settling === d.id ? null : d.id)}
                                  >
                                    {settling === d.id ? "Cancel" : "Record payment"}
                                  </Button>
                                )
                              )}
                            </div>
                          </Td>
                        </tr>
                        {openPayments.has(d.id) && bill && (
                          <tr>
                            <td colSpan={7} className="bg-white px-4 py-3">
                              <PaymentsList
                                slug={slug}
                                kind="bill"
                                id={bill.id}
                                currency={drawCurrency}
                              />
                            </td>
                          </tr>
                        )}
                        {settling === d.id && bill && (
                          <tr>
                            <td colSpan={7} className="bg-white px-4 py-4">
                              <RecordPaymentForm
                                settlement={bill}
                                currency={drawCurrency}
                                noun="bill"
                                dateLabel="Paid on"
                                busy={run.isPending}
                                onSubmit={(p) => {
                                  run.mutate(() =>
                                    unwrap(
                                      api.api.c[":slug"].payables.payments.$post({
                                        param: { slug },
                                        json: {
                                          contractorId: bill.partyId,
                                          paidOn: p.on,
                                          amount: p.amount,
                                          currency: drawCurrency,
                                          ...(p.method ? { method: p.method } : {}),
                                          ...(p.reference ? { reference: p.reference } : {}),
                                          applications: [{ billId: bill.id, amount: p.amount }],
                                        },
                                      }),
                                    ),
                                  );
                                  setSettling(null);
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </Table>
              )}
            </div>
          )}
        </Panel>
      </Section>

      {/* ---- costs and income booked straight to the job ---- */}
      <Section id="expenses" active={active}>
        <Panel
          title="Expenses"
          action={
            <Button
              onClick={() => {
                expenses.clearErrors();
                setAddingExpense(true);
              }}
            >
              Record expense
            </Button>
          }
        >
          {addingExpense && (
            <Modal title="Record expense" wide onClose={() => setAddingExpense(false)}>
              <ExpenseForm
                currency={ccy}
                codes={costCodes.data?.costCodes ?? []}
                projects={[]}
                lockedProjectId={projectId}
                bare
                errors={expenses.fieldErrors}
                formError={expenses.formError}
                saving={expenses.saving}
                submitLabel="Record expense"
                onSubmit={async (body) => {
                  await expenses.create(body);
                  setAddingExpense(false);
                }}
              />
            </Modal>
          )}

          {editingExpense && (
            <Modal title="Edit expense" wide onClose={() => setEditingExpense(null)}>
              <ExpenseForm
                // Keyed by row, so opening a different expense starts from that
                // one's values rather than the last one's half-typed edits.
                key={editingExpense.id}
                currency={ccy}
                codes={costCodes.data?.costCodes ?? []}
                projects={[]}
                lockedProjectId={projectId}
                initial={expenseToInitial(editingExpense)}
                bare
                errors={expenses.fieldErrors}
                formError={expenses.formError}
                saving={expenses.saving}
                submitLabel="Save changes"
                onSubmit={async (body) => {
                  await expenses.update({ id: editingExpense.id, body });
                  setEditingExpense(null);
                }}
              />
            </Modal>
          )}

          {expenses.query.isPending ? (
            <Loading />
          ) : expenses.query.isError ? (
            <ErrorNote>{(expenses.query.error as Error).message}</ErrorNote>
          ) : expenses.query.data.expenses.length === 0 ? (
            <Empty title="No expenses on this project yet">
              Anything booked here counts against this job's gross margin.
            </Empty>
          ) : (
            <>
              <TableSearch
                value={expensesTable.query}
                onChange={expensesTable.setQuery}
                placeholder="Filter expenses…"
              />
              <Table
                head={
                  <>
                    <SortTh table={expensesTable} sortKey="date">
                      Date
                    </SortTh>
                    <SortTh table={expensesTable} sortKey="description">
                      Description
                    </SortTh>
                    <SortTh table={expensesTable} sortKey="costCode">
                      Cost code
                    </SortTh>
                    <SortTh table={expensesTable} sortKey="amount" right>
                      Amount
                    </SortTh>
                    <Th />
                  </>
                }
              >
                {expensesTable.rows.map((e) => (
                  <tr
                    key={e.id}
                    className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                  >
                    <Td mono muted>
                      {e.incurredOn}
                    </Td>
                    <Td>
                      {e.description}
                      {e.templateId && (
                        <span className="ml-2 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
                          recurring
                        </span>
                      )}
                      {e.status === "void" && (
                        <span className="ml-2 text-xs text-red-600">voided</span>
                      )}
                    </Td>
                    <Td muted>
                      {e.costCode} {e.costCodeName}
                    </Td>
                    <Td right mono>
                      {fmt(e.amount, e.currency)}
                    </Td>
                    <Td right>
                      <div className="flex justify-end gap-1">
                        {/* A voided or template-generated row can't be edited —
                            the server rejects both, so don't offer the button. */}
                        {e.status !== "void" && !e.templateId && (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              expenses.clearErrors();
                              setEditingExpense(e);
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        {canDelete && (
                          <DeleteButton
                            busy={del.deleting}
                            onConfirm={() => del.remove("expense", e.id)}
                          />
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            </>
          )}
        </Panel>
      </Section>

      <Section id="commissions" active={active}>
        <Panel
          title="Commissions"
          action={
            <Button
              onClick={() => {
                commissions.clearErrors();
                setAddingCommission(true);
              }}
            >
              Record commission
            </Button>
          }
        >
          <p className="mb-3 max-w-prose text-sm text-neutral-500">
            Money this job earns outside the contract — a referral cut, a finder's fee, a management
            percentage. It counts as revenue here alongside what you invoice.
          </p>

          {addingCommission && (
            <Modal title="Record commission" wide onClose={() => setAddingCommission(false)}>
              <CommissionForm
                slug={slug}
                currency={ccy}
                projects={[]}
                lockedProjectId={projectId}
                clients={clients.data?.clients ?? []}
                contractors={contractors.data?.contractors ?? []}
                bare
                errors={commissions.fieldErrors}
                formError={commissions.formError}
                saving={commissions.saving}
                onSubmit={async (body) => {
                  await commissions.create(body);
                  setAddingCommission(false);
                }}
              />
            </Modal>
          )}

          {editingCommission && (
            <Modal title="Edit commission" wide onClose={() => setEditingCommission(null)}>
              <CommissionForm
                key={editingCommission.id}
                slug={slug}
                currency={ccy}
                projects={[]}
                lockedProjectId={projectId}
                clients={clients.data?.clients ?? []}
                contractors={contractors.data?.contractors ?? []}
                initial={commissionToInitial(editingCommission)}
                bare
                errors={commissions.fieldErrors}
                formError={commissions.formError}
                saving={commissions.saving}
                submitLabel="Save changes"
                onSubmit={async (body) => {
                  await commissions.update({ id: editingCommission.id, body });
                  setEditingCommission(null);
                }}
              />
            </Modal>
          )}

          {commissions.query.isPending ? (
            <Loading />
          ) : commissions.query.isError ? (
            <ErrorNote>{(commissions.query.error as Error).message}</ErrorNote>
          ) : commissions.query.data.commissions.length === 0 ? (
            <Empty title="No commissions on this project yet" />
          ) : (
            <>
              <TableSearch
                value={commissionsTable.query}
                onChange={commissionsTable.setQuery}
                placeholder="Filter commissions…"
              />
              <Table
                head={
                  <>
                    <SortTh table={commissionsTable} sortKey="date">
                      Date
                    </SortTh>
                    <SortTh table={commissionsTable} sortKey="description">
                      Description
                    </SortTh>
                    <SortTh table={commissionsTable} sortKey="from">
                      From
                    </SortTh>
                    <SortTh table={commissionsTable} sortKey="amount" right>
                      Amount
                    </SortTh>
                    <Th />
                  </>
                }
              >
                {commissionsTable.rows.map((cm) => (
                  <tr
                    key={cm.id}
                    className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                  >
                    <Td mono muted>
                      {cm.earnedOn}
                    </Td>
                    <Td>
                      {cm.description}
                      {cm.status === "void" && (
                        <span className="ml-2 text-xs text-red-600">voided</span>
                      )}
                      {cm.reference && (
                        <span className="ml-2 text-xs text-neutral-400">{cm.reference}</span>
                      )}
                    </Td>
                    <Td muted>{cm.clientName ?? cm.contractorName ?? "—"}</Td>
                    <Td right mono>
                      {fmt(cm.amount, cm.currency)}
                    </Td>
                    <Td right>
                      <div className="flex justify-end gap-1">
                        {/* The server refuses to edit a voided row. */}
                        {cm.status !== "void" && (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              commissions.clearErrors();
                              setEditingCommission(cm);
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        {canDelete && (
                          <DeleteButton
                            busy={del.deleting}
                            onConfirm={() => del.remove("commission", cm.id)}
                          />
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            </>
          )}
        </Panel>
      </Section>
    </>
  );
}

/**
 * One section's page. Rendering nothing when it isn't the open one is what
 * unmounts its forms, so a half-typed expense doesn't reappear after a trip
 * through the schedule and back.
 */
function Section({
  id,
  active,
  children,
}: {
  id: SectionId;
  active: SectionId;
  children: React.ReactNode;
}) {
  return active === id ? <>{children}</> : null;
}
